import { useState, useEffect } from 'react';
import { useFuncionariosFormacao, type FuncionarioFormacao, type NovoFuncionarioFormacaoInput } from '@/context/FuncionariosFormacaoContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { supabase } from '@/lib/supabase';
import { MUNICIPIO_FORMACAO_ID } from '@/lib/municipios';

interface Props {
  funcionario?: FuncionarioFormacao | null;
  onClose: () => void;
}

const STATUS_OPTS = ['ativo', 'inativo', 'licença'];

export default function FuncionarioFormacaoModal({ funcionario, onClose }: Props) {
  useModalOverflow();
  const { animating, handleClose } = useModalAnimation(onClose);
  const { unidades, addFuncionario, updateFuncionario, saving, error } = useFuncionariosFormacao();
  const isEdit = !!funcionario;

  const [nome, setNome] = useState(funcionario?.nome ?? '');
  const [carreira, setCarreira] = useState(funcionario?.carreira ?? '');
  const [sexo, setSexo] = useState<'M' | 'F'>(funcionario?.sexo ?? 'M');
  const [unidadeOrganica, setUnidadeOrganica] = useState(funcionario?.unidadeOrganica ?? '');
  const [subunidadeOrganica, setSubunidadeOrganica] = useState(funcionario?.subunidadeOrganica ?? '');
  const [email, setEmail] = useState(funcionario?.email ?? '');
  const [telefone, setTelefone] = useState(funcionario?.telefone ?? '');
  const [status, setStatus] = useState(funcionario?.status ?? 'ativo');
  const [dataAlteracaoCategoria, setDataAlteracaoCategoria] = useState(funcionario?.dataAlteracaoCategoria ?? '');
  const [categoriaAlterada, setCategoriaAlterada] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>();
  const [success, setSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  // Carreiras dinâmicas da base de dados
  const [carreirasLista, setCarreirasLista] = useState<string[]>([]);
  const [carreirasLoading, setCarreirasLoading] = useState(true);

  useEffect(() => {
    async function loadCarreiras() {
      setCarreirasLoading(true);
      const { data } = await supabase
        .from('carreiras')
        .select('nome')
        .or(`municipio_id.is.null,municipio_id.eq.${MUNICIPIO_FORMACAO_ID}`)
        .eq('ativo', true)
        .order('ordem');
      const nomes = (data ?? []).map((c) => c.nome as string).filter(Boolean);
      setCarreirasLista(nomes);
      // Se não houver carreira selecionada e existirem carreiras, pré-seleciona a primeira
      if (!carreira && nomes.length > 0) {
        setCarreira(nomes[0]);
      }
      setCarreirasLoading(false);
    }
    loadCarreiras();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (funcionario) {
      setNome(funcionario.nome);
      setCarreira(funcionario.carreira);
      setSexo(funcionario.sexo);
      setUnidadeOrganica(funcionario.unidadeOrganica);
      setSubunidadeOrganica(funcionario.subunidadeOrganica);
      setEmail(funcionario.email);
      setTelefone(funcionario.telefone);
      setStatus(funcionario.status);
      setDataAlteracaoCategoria(funcionario.dataAlteracaoCategoria ?? '');
      setCategoriaAlterada(false);
    }
  }, [funcionario]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!nome.trim()) errs.nome = 'O nome é obrigatório';
    if (!carreira) errs.carreira = 'A carreira é obrigatória';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleCarreiraChange(novaCarreira: string) {
    if (isEdit && funcionario && novaCarreira !== funcionario.carreira) {
      setCategoriaAlterada(true);
      if (!dataAlteracaoCategoria) {
        setDataAlteracaoCategoria(new Date().toISOString().split('T')[0]);
      }
    }
    setCarreira(novaCarreira);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaveError('');
    setSuccess('');
    if (!validate()) return;

    // Se a carreira mudou e há data de alteração, registar no histórico
    if (isEdit && funcionario && carreira !== funcionario.carreira && dataAlteracaoCategoria) {
      await supabase.from('historico_categorias').insert({
        funcionario_id: funcionario.id,
        categoria: carreira,
        data_inicio: dataAlteracaoCategoria,
      });
    }

    const input: NovoFuncionarioFormacaoInput = {
      nome: nome.trim(),
      carreira,
      sexo,
      unidadeOrganica,
      subunidadeOrganica,
      email,
      telefone,
      status,
      dataAlteracaoCategoria: dataAlteracaoCategoria || undefined,
    };
    let ok = false;
    let errMsg = '';
    if (isEdit && funcionario) {
      ok = await updateFuncionario(funcionario.id, input);
    } else {
      ok = await addFuncionario(input);
    }
    if (!ok) {
      // Tenta obter erro mais específico do contexto
      errMsg = error || 'Erro ao guardar. Tenta novamente ou contacta o administrador.';
    }
    if (ok) {
      setSuccess(isEdit ? 'Funcionário actualizado!' : 'Funcionário adicionado!');
      setTimeout(() => onClose(), 900);
    } else {
      setSaveError(errMsg);
    }
  }

  const unidadesPrincipais = unidades.filter((u) => u.tipo === 'unidade');
  const subunidades = unidades.filter((u) => u.tipo === 'subunidade');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto mx-4 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}>
        {/* Header */}
        <div className="px-7 pt-6 pb-4 border-b border-zinc-100 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-zinc-900">
              <i className={`${isEdit ? 'ri-edit-line' : 'ri-user-add-line'} text-white text-base`} />
            </div>
            <div>
              <h2 className="text-[16px] font-bold text-zinc-900">
                {isEdit ? 'Editar Funcionário' : 'Novo Funcionário'}
              </h2>
              <p className="text-[11px] text-zinc-400">Gestão da Formação</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="whitespace-nowrap w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-7 py-5 space-y-4">
          {success && (
            <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[12px] px-3 py-2 rounded-lg">
              <i className="ri-checkbox-circle-fill" />
              {success}
            </div>
          )}
          {saveError && (
            <div className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-[12px] px-3 py-2 rounded-lg">
              <i className="ri-error-warning-fill" />
              {saveError}
            </div>
          )}

          {/* Nome */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
              Nome completo <span className="text-rose-500">*</span>
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: João Silva"
              className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400 ${errors?.nome ? 'border-rose-400' : 'border-zinc-200'}`}
            />
            {errors?.nome && <p className="text-[11px] text-rose-500 mt-1">{errors.nome}</p>}
          </div>

          {/* Carreira + Sexo + Data Alteração */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                Carreira <span className="text-rose-500">*</span>
              </label>
              <select
                value={carreira}
                onChange={(e) => handleCarreiraChange(e.target.value)}
                disabled={carreirasLoading}
                className={`w-full px-3.5 py-2.5 text-[13px] border rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer disabled:opacity-50 ${errors?.carreira ? 'border-rose-400' : 'border-zinc-200'}`}
              >
                <option value="">{carreirasLoading ? 'A carregar...' : '— Escolher —'}</option>
                {carreirasLista.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {carreirasLista.length === 0 && !carreirasLoading && (
                <p className="text-[11px] text-amber-600 mt-1">
                  Nenhuma carreira activa. Cria uma em Administração {'>'} Carreiras.
                </p>
              )}
              {errors?.carreira && <p className="text-[11px] text-rose-500 mt-1">{errors.carreira}</p>}
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">
                Data da Alteração
                {categoriaAlterada && (
                  <span className="ml-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                    <i className="ri-alert-line text-[9px]" /> Alteração detectada
                  </span>
                )}
              </label>
              <input
                type="date"
                value={dataAlteracaoCategoria}
                onChange={(e) => setDataAlteracaoCategoria(e.target.value)}
                className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400"
              />
              <p className="text-[10px] text-zinc-400 mt-1">
                Data em que a carreira/categoria passou a vigorar
              </p>
            </div>
          </div>

          {/* Sexo */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Género</label>
            <div className="flex gap-2">
              {(['M', 'F'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSexo(s)}
                  className={`whitespace-nowrap flex-1 py-2.5 text-[13px] font-semibold rounded-lg border transition-all cursor-pointer ${
                    sexo === s
                      ? 'bg-zinc-900 text-white border-zinc-900'
                      : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'
                  }`}
                >
                  {s === 'M' ? 'Masculino' : 'Feminino'}
                </button>
              ))}
            </div>
          </div>

          {/* Unidade Orgânica */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Unidade Orgânica</label>
            <select
              value={unidadeOrganica}
              onChange={(e) => setUnidadeOrganica(e.target.value)}
              className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
            >
              <option value="">— Sem unidade —</option>
              {unidadesPrincipais.map((u) => (
                <option key={u.id} value={u.nome}>{u.nome}</option>
              ))}
            </select>
          </div>

          {/* Subunidade */}
          {subunidades.length > 0 && (
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Subunidade / Serviço</label>
              <select
                value={subunidadeOrganica}
                onChange={(e) => setSubunidadeOrganica(e.target.value)}
                className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400 cursor-pointer"
              >
                <option value="">— Sem subunidade —</option>
                {subunidades.map((u) => (
                  <option key={u.id} value={u.nome}>{u.nome}</option>
                ))}
              </select>
            </div>
          )}

          {/* Email + Telefone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@exemplo.pt"
                className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
              />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Telefone</label>
              <input
                type="tel"
                value={telefone}
                onChange={(e) => setTelefone(e.target.value)}
                placeholder="9XX XXX XXX"
                className="w-full px-3.5 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-800 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-400"
              />
            </div>
          </div>

          {/* Estado */}
          <div>
            <label className="block text-[12px] font-semibold text-zinc-700 mb-1.5">Estado</label>
            <div className="flex gap-2">
              {STATUS_OPTS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={`whitespace-nowrap flex-1 py-2 text-[12px] font-semibold rounded-lg border transition-all cursor-pointer capitalize ${
                    status === s
                      ? 'bg-zinc-900 text-white border-zinc-900'
                      : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'
                  }`}
                >
                  {s === 'ativo' ? 'Ativo' : s === 'inativo' ? 'Inativo' : 'Licença'}
                </button>
              ))}
            </div>
          </div>

          {/* Botões */}
          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-zinc-100">
            <button
              type="button"
              onClick={handleClose}
              className="whitespace-nowrap px-5 py-2.5 text-[13px] font-medium text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200 transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving || carreirasLoading || carreirasLista.length === 0}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 text-[13px] font-semibold text-white bg-zinc-900 rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <><div className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line text-sm animate-spin" /></div>A guardar...</>
              ) : (
                <><div className="w-4 h-4 flex items-center justify-center"><i className={`${isEdit ? 'ri-save-line' : 'ri-user-add-line'} text-sm`} /></div>{isEdit ? 'Guardar' : 'Adicionar'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
